import {
  ApdmReplyDraftReviewDetailResult,
  apdmReplyOutputCharacterLimit,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApdmReviewAction,
  apdmReviewActionInput,
  canReviewApdmDraft,
} from './reviewDraft';

export function useApdmDraftReview(storeId: number, draftId: string) {
  const { apdmReviewService: service } = useServiceFactory();
  const [detail, setDetail] = useState<ApdmReplyDraftReviewDetailResult | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [completed, setCompleted] = useState<ApdmReviewAction | null>(null);
  const current = useRef<ApdmReplyDraftReviewDetailResult | null>(null);
  const generation = useRef(0);
  const working = useRef(false);
  const load = useCallback(async () => {
    if (working.current) return;
    const version = ++generation.current;
    working.current = true;
    setBusy(true);
    setFailed(false);
    current.current = null;
    setDetail(null);
    setCompleted(null);
    try {
      const loaded = await service.detail({ draftId });
      if (version !== generation.current) return;
      if (loaded.draft.ref.draftId !== draftId)
        throw new Error('Review draft mismatch');
      current.current = loaded;
      setDetail(loaded);
    } catch {
      if (version === generation.current) setFailed(true);
    } finally {
      if (version === generation.current) {
        working.current = false;
        setBusy(false);
      }
    }
  }, [service, draftId]);
  useEffect(() => {
    const contextGeneration = generation;
    working.current = false;
    void load();
    return () => {
      contextGeneration.current++;
      current.current = null;
      working.current = false;
    };
  }, [load, storeId]);
  const submit = useCallback(
    async (action: ApdmReviewAction, text: string): Promise<boolean> => {
      const captured = current.current;
      if (working.current || !captured || completed) return false;
      if (!canReviewApdmDraft(captured.draft)) {
        current.current = null;
        setFailed(true);
        return false;
      }
      if (
        action === ApdmReviewAction.EditSend &&
        (!text.trim() || text.length > apdmReplyOutputCharacterLimit)
      ) {
        setFailed(true);
        return false;
      }
      const version = generation.current;
      const input = apdmReviewActionInput(captured);
      working.current = true;
      setBusy(true);
      setFailed(false);
      try {
        if (action === ApdmReviewAction.Discard) await service.discard(input);
        else if (action === ApdmReviewAction.EditSend)
          await service.editSend({ ...input, text });
        else await service.send(input);
        if (version !== generation.current) return false;
        current.current = null;
        setCompleted(action);
        return true;
      } catch {
        if (version === generation.current) {
          current.current = null;
          setFailed(true);
        }
        return false;
      } finally {
        if (version === generation.current) {
          working.current = false;
          setBusy(false);
        }
      }
    },
    [service, completed],
  );
  return {
    state: { detail, busy, failed, completed },
    actions: { load, submit },
  };
}
